// src/pages/Dashboard.jsx
import { useEffect, useMemo, useState } from "react";
import { signOut, onAuthStateChanged } from "firebase/auth";
import { auth, db } from "../firebase/firebase";
import { collection, getDocs, doc, getDoc, query, where } from "firebase/firestore";
import { useNavigate } from "react-router-dom";

const TABS = [
  { key: "publicada", label: "Publicadas" },
  { key: "pending",   label: "Por publicar" },
  { key: "inactiva",  label: "Inactivas" },
];

export default function Dashboard() {
  const [motos, setMotos] = useState([]);
  const [tab, setTab] = useState("publicada");
  const [search, setSearch] = useState("");

  // Perfil
  const [userName, setUserName] = useState("Usuario");
  const [role, setRole] = useState("");           // admin | sucursal | operaciones | agencia
  const [agencyId, setAgencyId] = useState("");   // para rol=agencia

  // NUEVO: sucursal por ID (para consultas) + nombre (sólo UI)
  const [branchId, setBranchId] = useState("");   // sucursalId estable (ej. "cdmx")
  const [branchName, setBranchName] = useState(""); // sucursal legible

  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Verifica sesión y carga perfil
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (!u) { navigate("/"); return; }
      try {
        const uref = doc(db, "users", u.uid);
        const usnap = await getDoc(uref);
        if (usnap.exists()) {
          const data = usnap.data();
          setUserName(data?.nombre || u.email || "Usuario");
          setRole(data?.rol || "");
          setAgencyId(data?.agenciaId || "");
          // Preferimos IDs canónicos
          setBranchId(data?.sucursalId || "");
          // Nombre legible para mostrar (fallbacks legacy)
          setBranchName(data?.sucursal || data?.sucursalNombre || "");
        } else {
          setUserName(u.email || "Usuario");
          setRole("");
          setAgencyId("");
          setBranchId("");
          setBranchName("");
        }
      } catch {
        setUserName("Usuario");
        setRole("");
        setAgencyId("");
        setBranchId("");
        setBranchName("");
      }
    });
    return () => unsub();
  }, [navigate]);

  // Cargar motos según rol
  useEffect(() => {
    (async () => {
      if (role === "") return;
      setLoading(true);
      try {
        const col = collection(db, "motos");
        let snap;

        if (role === "admin" || branchId === "global") {
          // Admin ve todas
          snap = await getDocs(col);
        } else if (role === "agencia") {
          if (agencyId) {
            const qAg = query(col, where("agenciaId", "==", agencyId));
            snap = await getDocs(qAg);
          } else if (branchId) {
            const qSc = query(col, where("sucursalId", "==", branchId));
            snap = await getDocs(qSc);
          } else {
            // nada que mostrar
            setMotos([]);
            setLoading(false);
            return;
          }
        } else if (branchId) {
          // Sucursal / Operaciones por sucursalId
          const qSc = query(col, where("sucursalId", "==", branchId));
          snap = await getDocs(qSc);
        } else {
          setMotos([]);
          setLoading(false);
          return;
        }

        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setMotos(list);
      } catch (e) {
        console.error("Error cargando motos:", e);
      } finally {
        setLoading(false);
      }
    })();
  }, [role, branchId, agencyId]);

  // Contadores
  const counts = useMemo(() => ({
    publicadas: motos.filter((m) => m.estatus === "publicada").length,
    pending:    motos.filter((m) => m.estatus === "pending").length,
    inactiva:   motos.filter((m) => m.estatus === "inactiva").length,
  }), [motos]);

  // Filtro por pestaña + búsqueda
  const filtra = useMemo(() => {
    const porEstatus = motos.filter((m) => m.estatus === tab);
    const q = search.trim().toLowerCase();
    if (!q) return porEstatus;

    const tokens = q.split(/\s+/).filter(Boolean);
    return porEstatus.filter((m) => {
      const nameLC = (m.nombre || "").toLowerCase();
      const codeLC = (m.codigoMoto || m.id || "").toLowerCase();
      const nameMatch = tokens.some((t) => nameLC.includes(t));
      const codeMatch = codeLC.includes(q);
      return nameMatch || codeMatch;
    });
  }, [motos, tab, search]);

  const logout = async () => {
    await signOut(auth);
    navigate("/");
  };

  // UI helpers
  const navItems = [
    { key: "publicada", label: "Publicadas", count: counts.publicadas },
    { key: "pending",   label: "Por publicar", count: counts.pending },
    { key: "inactiva",  label: "Inactivas", count: counts.inactiva },
  ];

  const isAdmin = role === "admin";
  const isAgency = role === "agencia";

  return (
    <div className="min-h-screen bg-gray-100 text-gray-900">
      <div className="flex min-h-screen">
        {/* Sidebar */}
        <aside className="w-64 bg-white border-r border-gray-200 flex flex-col">
          {/* Brand / header */}
          <div className="px-5 py-4 border-b border-gray-200">
            <div className="text-lg font-semibold">Motofeel Admin</div>
            {(role || branchName) && (
              <div className="text-xs text-gray-500 mt-1">
                {(role || "usuario")} {branchName ? `• ${branchName}` : ""}
              </div>
            )}
          </div>

          {/* Navegación */}
          <nav className="p-3 space-y-1 flex-1">
            {navItems.map((item) => {
              const active = tab === item.key;
              return (
                <button
                  key={item.key}
                  onClick={() => setTab(item.key)}
                  className={`w-full text-left px-3 py-2 rounded flex items-center justify-between
                    ${active ? "bg-gray-900 text-white" : "hover:bg-gray-100 text-gray-800"}`}
                >
                  <span>{item.label}</span>
                  <span className={`text-xs px-2 py-0.5 rounded ${active ? "bg-white/20" : "bg-gray-200"}`}>
                    {item.count}
                  </span>
                </button>
              );
            })}

            {/* Acciones según rol */}
            {isAdmin && (
              <button
                onClick={() => navigate("/nueva-moto-admin")}
                className="w-full mt-2 text-left px-3 py-2 rounded bg-gray-900 text-white hover:bg-gray-800"
              >
                + Nueva moto
              </button>
            )}

            {isAgency && (
              <button
                onClick={() => navigate("/adicionar-moto")}
                className="w-full mt-2 text-left px-3 py-2 rounded bg-gray-900 text-white hover:bg-gray-800"
              >
                Adicionar moto
              </button>
            )}

            {/* Admin: gestión de catálogos y config */}
            {isAdmin && (
              <>
                <button
                  onClick={() => navigate("/admin/agencias")}
                  className="w-full text-left px-3 py-2 rounded hover:bg-gray-100 text-gray-800"
                >
                  ⚙️ Agencias
                </button>
                <button
                  onClick={() => navigate("/admin/sucursales")}
                  className="w-full text-left px-3 py-2 rounded hover:bg-gray-100 text-gray-800"
                >
                  🏢 Sucursales
                </button>
                <button
                  onClick={() => navigate("/admin/users")}
                  className="w-full text-left px-3 py-2 rounded hover:bg-gray-100 text-gray-800"
                >
                  👤 Usuarios
                </button>
                <button
                  onClick={() => navigate("/admin/config")}
                  className="w-full text-left px-3 py-2 rounded hover:bg-gray-100 text-gray-800"
                >
                  🔧 Configuración
                </button>
                <button
                  onClick={() => navigate("/admin/sellers")}
                  className="w-full text-left px-3 py-2 rounded hover:bg-gray-100 text-gray-800"
                >
                  🧩 Sellers
                </button>
                <button
                  onClick={() => navigate("/admin/publicaciones")}
                  className="w-full text-left px-3 py-2 rounded hover:bg-gray-100 text-gray-800"
                >
                  🗞️ Publicaciones
                </button>
              </>
            )}
          </nav>

          {/* Footer */}
          <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm truncate max-w-[140px]" title={userName}>{userName}</div>
            <button
              onClick={logout}
              className="px-3 py-1.5 text-sm rounded bg-gray-900 text-white hover:bg-gray-800"
            >
              Salir
            </button>
          </div>
        </aside>

        {/* Main */}
        <main className="flex-1">
          {/* Topbar */}
          <header className="px-6 py-4 border-b border-gray-200 bg-white">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <h1 className="text-xl font-semibold">
                {TABS.find(t => t.key === tab)?.label || "Listado"}
              </h1>
              <div className="w-full md:w-96">
                <input
                  type="text"
                  placeholder="Buscar por nombre o código…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full px-3 py-2 rounded border border-gray-300 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900"
                />
              </div>
            </div>
          </header>

          {/* Contenido */}
          <section className="px-6 py-6">
            {loading ? (
              <div className="text-gray-500">Cargando motos…</div>
            ) : filtra.length === 0 ? (
              <div className="text-gray-500">No hay motos que coincidan.</div>
            ) : (
              <div className="space-y-3">
                {filtra.map((m) => {
                  const foto = m.foto1 || m.foto2 || m.foto3 || m.foto4 || m.foto5 || m.foto6 || m.foto7;
                  return (
                    <div
                      key={m.id}
                      onClick={() => navigate(`/detalle/${m.id}`)}
                      className="cursor-pointer rounded-lg border border-gray-200 hover:border-gray-900 bg-white transition p-3 flex gap-3"
                    >
                      {/* Imagen */}
                      <div className="w-36 h-24 flex-shrink-0 overflow-hidden rounded border border-gray-200 bg-gray-100">
                        {foto ? (
                          <img src={foto} alt={m.nombre} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-400 text-sm">
                            Sin foto
                          </div>
                        )}
                      </div>

                      {/* Datos */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <h3 className="font-semibold truncate">{m.nombre}</h3>
                          <span
                            className={`text-xs px-2 py-0.5 rounded border ${
                              m.estatus === "publicada"
                                ? "border-emerald-500 text-emerald-600"
                                : m.estatus === "inactiva"
                                ? "border-gray-400 text-gray-600"
                                : "border-amber-500 text-amber-600"
                            }`}
                          >
                            {m.estatus}
                          </span>
                        </div>
                        <div className="text-gray-600 text-sm mt-1 flex flex-wrap gap-x-4 gap-y-1">
                          <span>{m.marca} • {m.modelo}</span>
                          {(m.sucursalNombre || m.sucursal) && (
                            <span>Sucursal: {m.sucursalNombre || m.sucursal}</span>
                          )}
                          {typeof m.valor === "number" && !Number.isNaN(m.valor) && (
                            <span className="text-gray-900 font-medium">
                              ${m.valor.toLocaleString("es-MX")}
                            </span>
                          )}
                          {m.codigoMoto && <span className="text-gray-400">[{m.codigoMoto}]</span>}
                        </div>
                        {m.descripcion && (
                          <p className="text-gray-600 text-sm line-clamp-2 mt-1">
                            {m.descripcion}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        </main>
      </div>
    </div>
  );
}